// /users — player search page (Wave 2)
// Spec: docs/plans/admin-tooling.md §14.1–§14.2

import { useNavigate } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { Button, Input, Field, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { apiFetch } from '@/lib/api';
import { StatusPill, type PillStatus } from '@/components/admin/status-pill';

interface UserSummary {
  user_id: number;
  username: string;
  login_name: string;
  email: string;
  active: number;
  locked_at: number | null;
  level: number;
}

type SearchState = 'idle' | 'loading' | 'done' | 'error';

export function UsersPage() {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>('idle');
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function runSearch(q: string) {
    const trimmed = q.trim();
    if (!trimmed) {
      setUsers([]);
      setState('idle');
      return;
    }

    // Allow single-digit (numeric user_id) but enforce 2-char min otherwise.
    const isNumeric = /^\d+$/.test(trimmed);
    if (!isNumeric && trimmed.length < 2) {
      return;
    }

    setState('loading');
    setErrorMsg('');

    const res = await apiFetch<{ ok: boolean; users: UserSummary[]; error?: string }>(
      `/admin/users?q=${encodeURIComponent(trimmed)}`,
    );

    if (!res.ok) {
      setState('error');
      setErrorMsg('Search failed. Please try again.');
      return;
    }

    setUsers(res.data.users ?? []);
    setState('done');
  }

  function handleInputChange(value: string) {
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSearch(value);
    }, 300);
  }

  function handleSearchClick() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    void runSearch(query);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void runSearch(query);
    }
  }

  function handleRowClick(userId: number) {
    void navigate({ to: '/users/$user_id', params: { user_id: String(userId) } });
  }

  function userPillStatus(user: UserSummary): PillStatus {
    if (user.locked_at !== null) return 'locked';
    if (!user.active) return 'inactive';
    return 'active';
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Players"
        description="Search by login name, email, user ID, or IP address."
      />

      {/* Search bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
        <div className="flex-1">
          <Field label="Search">
            <Input
              id="player-search"
              type="text"
              placeholder="login name, email, user ID, or IP…"
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={state === 'loading'}
              autoComplete="off"
            />
          </Field>
        </div>
        <Button
          type="button"
          onClick={handleSearchClick}
          disabled={state === 'loading'}
        >
          {state === 'loading' ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              Searching…
            </span>
          ) : (
            'Search'
          )}
        </Button>
      </div>

      {/* Error */}
      {state === 'error' && (
        <ErrorChip>{errorMsg}</ErrorChip>
      )}

      {/* Loading spinner */}
      {state === 'loading' && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Results */}
      {state === 'done' && users.length === 0 && (
        <p className="text-sm text-ink-low">No players found.</p>
      )}

      {state === 'done' && users.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">User ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Login name</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Username</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Level</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="cursor-pointer hover:bg-panel-elev transition-colors"
                    onClick={() => handleRowClick(u.user_id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(u.user_id);
                    }}
                    data-testid={`user-row-${u.user_id}`}
                  >
                    <td className="px-3 py-2 text-ink-low font-mono tabular-nums text-xs">{u.user_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{u.login_name}</td>
                    <td className="px-3 py-2">{u.username}</td>
                    <td className="px-3 py-2 tabular-nums">{u.level}</td>
                    <td className="px-3 py-2"><StatusPill status={userPillStatus(u)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden">
            {users.map((u) => (
              <button
                key={u.user_id}
                type="button"
                className="w-full rounded-sm border border-border bg-panel p-4 text-left hover:bg-panel-elev transition-colors min-h-[44px]"
                onClick={() => handleRowClick(u.user_id)}
                data-testid={`user-card-${u.user_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5">
                    <div className="font-medium text-ink-high">{u.username}</div>
                    <div className="text-xs text-ink-low font-mono">{u.login_name}</div>
                    <div className="text-xs text-ink-low font-mono tabular-nums">ID {u.user_id} · Level {u.level}</div>
                  </div>
                  <div><StatusPill status={userPillStatus(u)} /></div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
